import React, { PureComponent } from 'react';
import { Grid, Colors, AnalyticsPage } from '@inplayer-org/inplayer-ui';
import styled from 'styled-components';
import { transparentize } from 'polished';

// types
import { BrandingDetails } from 'ducks/merchantDucks/branding/types';
import { Feature } from 'ducks/merchantDucks/user/types';

// utils
import isEqual from 'lodash/isEqual';

// images
import previewImg from 'assets/pics/ip-preview-premium.jpg';

// constants
import Rules from 'restrictions/roles/constants/rules';

// components
import PreviewTemplateBox from 'pages/MerchantPages/Assets/PreviewTemplates/PreviewTemplateBox';
import PlainRegisterScreen from 'components/PaywallPreviews/PlainRegisterScreen';
import CustomTabNavigation from 'pages/MerchantPages/Analytics/components/CustomTabNavigation';
import PaywallPreview from 'components/PaywallPreviews/AssetPreview';
import PaywallConfigurationFrom from './PaywallConfigurationForm';

const { Container, Cell } = Grid;

const PaywallPreviewWrapper = styled(Container)`
  padding: 2rem;
  background: ${Colors.white};
  border-radius: 5px;
  box-shadow: 0 0 4px 1px ${transparentize(0.87, Colors.fontDarkGray)};
`;

const PaywallModalWrapper = styled(PaywallPreviewWrapper)`
  margin-top: 2rem;
`;

const PreviewTemplateBoxWrapper = styled.div`
  margin: 2rem;
`;

const PreviewContainer = styled(Cell)`
  width: 90%;
  margin: auto;

  @media screen and (max-width: 1300px) {
    width: 100%;
  }
`;

const INPLAYER_LOGO_URL = 'https://assets.inplayer.com/images/inplayer_full-logo-dark-blue-t.png';

interface OwnProps {
  branding: BrandingDetails;
  previewCoverImage: string;
  previewLogoImage: string;
  features: Array<Feature>;
  resetForm: () => any;
  uploadCoverImage: (file: File) => any;
  uploadLogoImage: (file: File) => any;
  clearLogo: () => void;
  hasRule: (rule: Rules) => boolean;
}

export interface Branding {
  isDefault?: boolean;
  paywall_cover_photo?: string;
  preview_top_border?: boolean;
  inplayer_protected_label?: boolean;
  preview_buttons_bg_color?: string;
  preview_buttons_text_color?: string;
  paywall_brand_logo?: string;
  paywall_primary_color?: string;
  paywall_secondary_color?: string;
  paywall_buttons_bg_color?: string;
  paywall_buttons_text_color?: string;
  displayBtnColorPicker?: boolean;
  displayTextColorPicker?: boolean;
  displayPrimaryColorPicker?: boolean;
  displaySecondaryColorPicker?: boolean;
  displayModalBtnColorPicker?: boolean;
  displayModalTextColorPicker?: boolean;
  paywall_footer?: string;
  template_id?: number;
  terms_url?: string;
  dark_mode?: boolean;
  [key: string]: any;
}

interface State {
  branding: Branding;
  isLogoShown: boolean;
  selectedTabIndex: number;
}

const paywallTabs = [
  { name: 'Preview configuration', tag: 'preview_configuration' },
  { name: 'Preview templates', tag: 'preview_templates' },
];

export default class Preview extends PureComponent<OwnProps, State> {
  state = {
    branding: {
      isDefault: false,
      paywall_cover_photo: previewImg,
      preview_top_border: true,
      inplayer_protected_label: true,
      preview_buttons_bg_color: '#56c0a4',
      preview_buttons_text_color: '#FFFFFF',
      paywall_brand_logo: INPLAYER_LOGO_URL,
      paywall_primary_color: '#52ceb0',
      paywall_secondary_color: '#44a7c8',
      paywall_buttons_bg_color: '#56c0a4',
      paywall_buttons_text_color: '#FFFFFF',
      displayBtnColorPicker: false,
      displayTextColorPicker: false,
      displayPrimaryColorPicker: false,
      displaySecondaryColorPicker: false,
      displayModalBtnColorPicker: false,
      displayModalTextColorPicker: false,
      paywall_footer:
        '<p>Copyright © 2018 InPlayer LLC, All rights reserved. <a href="https://inplayer.com/terms/" target="_blank" data-text="modal_terms">Terms</a>, <a href="https://inplayer.com/paywall-faq/" target="_blank" data-text="modal_faq">FAQ</a></p>',
      template_id: 1,
      terms_url: '',
      dark_mode: false,
    },
    isLogoShown: false,
    selectedTabIndex: 0,
  };

  componentDidMount() {
    const { branding } = this.props;
    const { branding: stateBranding } = this.state;

    if (branding.id) {
      this.setState({
        branding: {
          ...stateBranding,
          ...branding,
          template_id: branding.template_id === null ? 1 : branding.template_id,
          paywall_cover_photo: branding.paywall_cover_photo === '' ? previewImg : branding.paywall_cover_photo,
          isDefault: branding.default,
        },
        isLogoShown: !!branding.paywall_brand_logo,
      });
    }
  }

  componentDidUpdate(prevProps: OwnProps) {
    const {
      branding: prevBranding,
      previewCoverImage: prevPreviewCoverImage,
      previewLogoImage: prevPreviewLogoImage,
    } = prevProps;
    const { branding: stateBranding } = this.state;
    const { branding, previewCoverImage, previewLogoImage } = this.props;

    if (!isEqual(prevBranding, branding)) {
      if (prevBranding.id) {
        this.setState({
          branding: {
            ...stateBranding,
            ...branding,
            template_id: branding.template_id === null ? 1 : branding.template_id,
            paywall_cover_photo: branding.paywall_cover_photo === '' ? previewImg : branding.paywall_cover_photo,
            isDefault: branding.default,
          },
        });
      }
    }

    if (previewCoverImage && prevPreviewCoverImage !== previewCoverImage) {
      this.setState({
        branding: {
          ...stateBranding,
          paywall_cover_photo: previewCoverImage,
        },
      });
    }

    if (previewLogoImage && prevPreviewLogoImage !== previewLogoImage) {
      this.setState({
        branding: {
          ...stateBranding,
          paywall_brand_logo: previewLogoImage,
        },
      });
    }
  }

  getValues = () => {
    const {
      branding: {
        isDefault,
        paywall_cover_photo,
        preview_top_border,
        inplayer_protected_label,
        preview_buttons_bg_color,
        preview_buttons_text_color,
        paywall_brand_logo,
        paywall_primary_color,
        paywall_secondary_color,
        paywall_buttons_bg_color,
        paywall_buttons_text_color,
        paywall_footer,
        template_id,
        terms_url,
        dark_mode,
      },
    } = this.state;

    return {
      default: isDefault,
      paywall_cover_photo,
      preview_top_border,
      inplayer_protected_label,
      preview_buttons_bg_color,
      preview_buttons_text_color,
      paywall_brand_logo,
      paywall_primary_color,
      paywall_secondary_color,
      paywall_buttons_bg_color,
      paywall_buttons_text_color,
      paywall_footer,
      template_id,
      terms_url,
      dark_mode,
    };
  };

  handleChange = (input: string, value: boolean | string) => {
    this.setState(({ branding }) => ({
      branding: {
        ...branding,
        [input]: value,
      },
    }));
  };

  handleClick = (input: string) => () => {
    this.setState(({ branding }) => ({
      branding: {
        ...branding,
        [input]: !branding[input],
      },
    }));
  };

  handleClose = (input: string) => () => {
    this.setState(({ branding }) => ({
      branding: {
        ...branding,
        [input]: false,
      },
    }));
  };

  handleShowLogoToggle = (checked: boolean) => () => {
    const { branding } = this.state;
    const { clearLogo } = this.props;

    const logoToSave = !checked ? '' : INPLAYER_LOGO_URL;

    this.setState({
      isLogoShown: checked,
      branding: {
        ...branding,
        paywall_brand_logo: logoToSave,
      },
    });

    if (!checked) {
      clearLogo();
    }
  };

  handleTabChange = (selectedIndex: number) => {
    this.setState({ selectedTabIndex: selectedIndex });
  };

  handlePreviewChange = (selectedTemplateId: number) => {
    const { branding } = this.state;
    this.setState({ branding: { ...branding, template_id: selectedTemplateId } });
  };

  uploadLogoImage = (file: File) => {
    const { uploadLogoImage } = this.props;

    this.setState({ isLogoShown: true });

    uploadLogoImage(file);
  };

  render() {
    const { resetForm, uploadCoverImage, hasRule, features } = this.props;
    const { branding, isLogoShown, selectedTabIndex } = this.state;
    const { template_id: previewTemplateId } = branding;
    const isTermsFeatureEnabled =
      features.some(({ name, enabled }: Feature) => name === 'terms' && enabled) &&
      !hasRule(Rules.READONLY_BRANDING_THEMES);

    return (
      <>
        <AnalyticsPage tag={paywallTabs[selectedTabIndex].tag} type="tab">
          <PaywallPreviewWrapper columns={2}>
            <Cell>
              <CustomTabNavigation
                tabs={paywallTabs}
                selectedTabIndex={selectedTabIndex}
                onTabClick={this.handleTabChange}
              />
              {selectedTabIndex === 0 ? (
                <PaywallConfigurationFrom
                  title="Thumbnail"
                  resetForm={resetForm}
                  uploadImage={uploadCoverImage}
                  configTitle="Elements"
                  branding={branding}
                  previewTemplateId={previewTemplateId}
                  handleChange={this.handleChange}
                  handleClick={this.handleClick}
                  handleClose={this.handleClose}
                />
              ) : (
                <PreviewTemplateBoxWrapper>
                  <PreviewTemplateBox
                    handlePreviewChange={this.handlePreviewChange}
                    previewTemplateId={previewTemplateId}
                  />
                </PreviewTemplateBoxWrapper>
              )}
            </Cell>
            <PreviewContainer>
              <PaywallPreview
                previewTemplateId={previewTemplateId}
                branding={branding}
                defaultAssePreviewDetails
                displayBuyButton
              />
            </PreviewContainer>
          </PaywallPreviewWrapper>
        </AnalyticsPage>
        <PaywallModalWrapper columns={2}>
          <Cell top={2}>
            <PaywallConfigurationFrom
              title="Logo"
              resetForm={resetForm}
              uploadImage={this.uploadLogoImage}
              configTitle="Paywall"
              branding={branding}
              handleChange={this.handleChange}
              handleClick={this.handleClick}
              handleClose={this.handleClose}
              isLogoShown={isLogoShown}
              handleShowLogoToggle={this.handleShowLogoToggle}
              showLogoConfig
              isTermsFeatureEnabled={isTermsFeatureEnabled}
            />
          </Cell>
          <Cell top={2}>
            <PlainRegisterScreen branding={branding} hideLogo={isLogoShown} />
          </Cell>
        </PaywallModalWrapper>
      </>
    );
  }
}
